import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class SentryService {

  // Holds the active call-session span. Null when no call is in progress.
  private callSessionSpan: Sentry.Span | null = null;

  setUser(userId: string, additionalData?: Record<string, any>) {
    Sentry.setUser({ id: userId, ...additionalData });
  }

  clearUser() {
    Sentry.setUser(null);
  }

  setContext(key: string, value: Record<string, any>) {
    Sentry.setContext(key, value);
  }

  setTag(key: string, value: string | number | boolean) {
    Sentry.setTag(key, value);
  }

  captureException(error: any, context?: Record<string, any>, tags?: Record<string, string>) {
    Sentry.captureException(error, { extra: context, tags });
  }

  /**
   * Opens a Sentry transaction that spans the active call session.
   * tracesSampler in main.ts detects op='call.simulation' and forces 100% sampling,
   * so every HTTP call, WebSocket event and span created while this transaction is
   * active is recorded with full fidelity.
   *
   * Safe to call multiple times — a second call while a session is already open is
   * a no-op, preventing duplicate transactions if startCall is triggered again.
   */
  startCallSession(callUuid: string, moduleAssignmentUuid: string): void {
    if (this.callSessionSpan) return;

    this.callSessionSpan = Sentry.startInactiveSpan({
      name: 'call-session',
      op: 'call.simulation',
      forceTransaction: true,
      attributes: { callUuid, moduleAssignmentUuid },
    });
  }

  /**
   * Closes the active call-session transaction.
   * Must be called on every exit path from a call (normal end, force end, realtime end).
   * Safe to call when no session is open — no-op in that case.
   */
  endCallSession(): void {
    this.callSessionSpan?.end();
    this.callSessionSpan = null;
  }
  startReplay(): void {
    const replay = Sentry.getReplay();
    if (!replay) return;
    replay.start();
  }

  stopReplay(): void {
    const replay = Sentry.getReplay();
    if (!replay) return;
    replay.stop();
  }

}
